import { Button, CircularProgress, Fade, Grid, IconButton, InputAdornment, TextField } from '@mui/material'
import { Link, Navigate } from 'react-router-dom'
import { styled } from '@mui/system'
import { useSnackbar } from 'notistack'

import logo from '../resources/images/logo.png'
import { FormEvent, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { cognitoSignin } from '../features/auth/cognitoAuth'

const Logo = styled('img')({
  width: 200,
  marginBottom: 40
})

export default function Login(){

  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
  } = useAuth()
  const logged = useMemo(() => isAuthenticated && user, [isAuthenticated, user]);
  const { enqueueSnackbar } = useSnackbar();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const loading = authLoading || submitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await cognitoSignin(username, password);
    } catch (err: any) {
      enqueueSnackbar(
        err?.message || 'Credenziali non valide. Riprova.',
        { variant: 'error', preventDuplicate: true }
      );
    } finally {
      setSubmitting(false);
    }
  }

  if(logged)
    return <Navigate replace to="/" />

  return <Grid
    container justifyContent="center"
    sx={{
      width: 1, height: '100vh', background: '#f2f2f2'
    }}
  >
    <Fade in={true} exit={true} mountOnEnter unmountOnExit>
    <Grid
      size={{ xs: 8, sm: 6, md: 4, lg: 3 }}
      container
      direction="column"
      justifyContent="center"
      alignItems="stretch"
      spacing={2}
      component="form"
      onSubmit={handleSubmit}
    >
      <Grid container justifyContent="center">
        <Logo src={logo} alt="logo" />
      </Grid>
      <Grid>
        <TextField
          label="Email / Username"
          variant="standard"
          fullWidth
          required
          autoComplete="username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          disabled={loading}
        />
      </Grid>
      <Grid>
        <TextField
          label="Password"
          variant="standard"
          fullWidth
          required
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          disabled={loading}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="mostra password"
                    onClick={() => setShowPassword(v => !v)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>
      <Grid container justifyContent="center" sx={{ minHeight: 36 }}>
        {loading && <CircularProgress size={28} />}
      </Grid>
      <Grid>
        <Button
          variant="contained"
          fullWidth
          type="submit"
          disabled={loading}
        >
          Accedi
        </Button>
      </Grid>
      <Grid>
        <Button
          variant="text"
          fullWidth
          component={Link}
          to="/recover"
          disabled={loading}
        >
          Password dimenticata?
        </Button>
      </Grid>
      <Button
        variant="text" size="small" color="primary"
        sx={{
          position: 'fixed', bottom: theme => theme.spacing(1),
          left: 0, right: 0, margin: 'auto', width: 'fit-content',
        }}
        component={Link}
        to="/privacy"
      >
        Privacy Policy
      </Button>
    </Grid>
    </Fade>
  </Grid>
}